import React, { FC, useState } from "react";
import { Box, Typography, Button, IconButton, useMediaQuery, Select, MenuItem } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { VariationReviewModal } from "../videoReview/VariationReviewModal";
import { PublicUrlMenu, buildPublicUrlOptions } from "../../../../components/PublicUrlMenu";
import { BrandProject } from "../constants";
import { EmptyPanel } from "../../../brand/projects/common/EmptyPanel";
import { VideoCard } from "../../../../components/videoSection/VideoCard";
import { UploadableVideoCard, ChannelUploadTile, canUploadRole } from "../../../../components/videoSection/VideoUpload";
import { PageTitle } from "../../../../components/PageTitle";
import { IVideo } from "../../../admin/videos/hooks/useFetchVideos";
import { getBrandAllowedFileTypes } from "../../../admin/videos/selectedVideoCardV2/helpers";
import { useUser } from "../../../../hooks/useUser";
import { isBrand, isInternal } from "../../../../utils";
import { ViewToggle } from "../../../../components/ViewToggle/ViewToggle";
import { ProjectDownloadBtn } from "../ProjectDownloadBtn";
import { useChannelGroups, ChannelGroup } from "../../../../hooks/useChannelGroups";
import { ChannelAccordion } from "../shared/ChannelAccordion";
import { ProjectChannelOptionsModal } from "../shared/ProjectChannelOptionsModal";
import { BRAND_PROJECT_STATUSES, filterRealFiles, internalStatusOptionsFor } from "../../helper";
import axiosInstant from "../../../../api";

// Design §02: internal_status is a CONTROL, so it looks like one — a select,
// not a pill wearing select behavior. Shared by the per-deliverable and the
// project-level pickers so the two read as the same control.
const INTERNAL_STATUS_SELECT_SX = {
    fontSize: 11.5,
    fontWeight: 700,
    fontFamily: "CircularStd",
    height: 30,
    borderRadius: "8px",
    backgroundColor: "var(--surface-2)",
    color: "var(--text)",
    "& .MuiOutlinedInput-notchedOutline": { border: "1px solid var(--border)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { border: "1px solid var(--accent)" },
    "& .MuiSelect-select": { py: 0, px: "10px", display: "flex", alignItems: "center" },
    "& .MuiSelect-icon": { color: "var(--muted)" }
};

// Sentinel for the project-level picker's "Clear" option ("" is its placeholder).
const CLEAR_ALL_STATUS = "__clear__";

interface Props {
    project: BrandProject;
    onProjectUpdate?: () => void;
    /** Enables the richer per-slot upload UX (progress + granular refresh).
     *  Passed only from the project-details Downloads tab. */
    uploadUx?: boolean;
}

export const Downloads: FC<Props> = (props) => {
    const { project, onProjectUpdate, uploadUx = false } = props;
    const navigate = useNavigate();
    const { role } = useUser();
    // §09 one mobile rule: rows below Tailwind md (768px) — the same query
    // VideoSection uses. MUI's own `md` is 900px and would overshoot.
    const isMobile = useMediaQuery("(max-width: 767.95px)");
    const [view, setView] = useState<"grid" | "list">("list");
    const effectiveView = isMobile ? "list" : view;
    const [editingGroup, setEditingGroup] = useState<ChannelGroup | null>(null);

    // Same rule as the project row's download icon (DownloadStatusIcon), plus
    // the share page: a brand user can download a Final Delivered project.
    const canDownload =
        isInternal(role) ||
        !!project.brands?.some((b) => b.pre_approval_downloading) ||
        !!project.download_page ||
        (isBrand(role) && project.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED);

    const videos = project?.projectFiles;
    const allowedFileTypes = isInternal(role)
        ? null
        : getBrandAllowedFileTypes(project?.brands as any);

    const [searchParams, setSearchParams] = useSearchParams();
    const reviewFileId = searchParams.get("review");
    const reviewFile = reviewFileId
        ? (videos || []).find((v) => String(v.id) === String(reviewFileId))
        : undefined;
    const closeReview = () => {
        setSearchParams((params) => {
            params.delete("review");
            params.delete("comment");
            params.delete("t");
            return params;
        });
    };

    const { channelGroups, hasChannels } = useChannelGroups(
        project?.content_channels || [],
        videos || []
    );

    if (!videos?.length && !hasChannels)
        return (
            <Box className="flex justify-center items-center h-full my-12">
                <EmptyPanel showSpacing text="There are no videos for this project. Check back soon!" />
            </Box>
        );

    const publicUrlOptions = buildPublicUrlOptions(project, isInternal(role) || isBrand(role));

    const handleVideoSelect = (video: IVideo) => {
        if (!video.key && !video.s3Location) return;
        const base = isInternal(role) ? "/videos" : "/videos/tdc";
        navigate(`${base}?video=${video.id}`);
    };

    const uploadEnabled = uploadUx && canUploadRole(role);
    const renderVideoCard = (video: IVideo) => {
        const variant = effectiveView === "grid" ? ("browse" as const) : ("row" as const);
        const card = (uploadSlot: React.ReactNode) => (
            <VideoCard
                video={video}
                variant={variant}
                onOpen={handleVideoSelect}
                refetch={onProjectUpdate || (() => {})}
                project={project}
                renderInternalStatus={isInternal(role) ? renderInternalStatus : undefined}
                uploadSlot={uploadSlot}
            />
        );
        return uploadEnabled ? (
            <UploadableVideoCard key={video.id} video={video} project={project} refetch={onProjectUpdate || (() => {})}>
                {card}
            </UploadableVideoCard>
        ) : (
            <React.Fragment key={video.id}>{card(null)}</React.Fragment>
        );
    };
    const renderVideos = (vids: IVideo[]) => {
        if (!vids.length) {
            return (
                <Typography className="text-text-2 py-6 text-center" sx={{ fontSize: 13 }}>
                    No deliverables here yet.
                </Typography>
            );
        }
        return effectiveView === "grid" ? (
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
                {vids.map(renderVideoCard)}
            </div>
        ) : (
            <div className="flex flex-col gap-3">{vids.map(renderVideoCard)}</div>
        );
    };

    const handleDeliverableStatusChange = async (video: IVideo, value: string) => {
        const brandId = project.brands?.find((b) => !b.rmn)?.id || project.brands?.[0]?.id || project.brand_id;
        if (!brandId || !project?.id || video?.id == null) return;
        // internal_status lives on the deliverable (s3_file). Send this one
        // deliverable's id; the API writes the value across its whole version
        // lineage, so it sticks as new versions are uploaded.
        try {
            await axiosInstant.patch(`/brands/${brandId}/projects/${project.id}`, {
                deliverable_internal_statuses: { [video.id]: value || null }
            });
            toast.success("Internal status updated");
            onProjectUpdate?.();
        } catch (error) {
            console.error("Internal status update failed:", error);
            toast.error("Failed to update internal status");
        }
    };

    // Project-level set: every real deliverable to one status in ONE PATCH, so
    // the API sends a single notification (like Approve All). The API skips
    // lineages already at the target and collapses version lineages itself.
    const realVideos = filterRealFiles(videos);
    const sharedInternalStatus =
        realVideos.length > 0 && realVideos.every((v) => v.internal_status === realVideos[0].internal_status)
            ? realVideos[0].internal_status || ""
            : "";
    const handleProjectStatusChange = async (value: string) => {
        const brandId = project.brands?.find((b) => !b.rmn)?.id || project.brands?.[0]?.id || project.brand_id;
        if (!brandId || !project?.id || !realVideos.length) return;
        const status = value === CLEAR_ALL_STATUS ? null : value;
        const toastId = toast.loading("Updating internal status...");
        try {
            await axiosInstant.patch(`/brands/${brandId}/projects/${project.id}`, {
                deliverable_internal_statuses: Object.fromEntries(realVideos.map((v) => [v.id, status]))
            });
            toast.success(
                status
                    ? `Internal status set to "${status}" for all videos.`
                    : "Internal status cleared for all videos.",
                { id: toastId }
            );
            onProjectUpdate?.();
        } catch (error: any) {
            console.error("Project internal status update failed:", error);
            toast.error(error?.response?.data?.error || "Failed to update internal status", { id: toastId });
        }
    };

    // Per-deliverable internal-status dropdown shown next to each video in the
    // Downloads list. Status is stored on the deliverable, so the control is per
    // deliverable — not per channel. Passed to VideosList only for internal
    // users (see below), so brand views render unchanged.
    const renderInternalStatus = (video: IVideo) => (
        <Select
            size="small"
            displayEmpty
            value={video.internal_status || ""}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleDeliverableStatusChange(video, e.target.value)}
            sx={INTERNAL_STATUS_SELECT_SX}
        >
            <MenuItem value="">
                <em>Internal Status</em>
            </MenuItem>
            {internalStatusOptionsFor(video.internal_status).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                </MenuItem>
            ))}
        </Select>
    );

    return (
        <Box className="flex flex-col px-6">
            <Box className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <PageTitle variant="h5" title="Videos" />
                <Box className="flex flex-wrap items-center gap-3">
                    {project?.id && (
                        <ProjectDownloadBtn
                            projectId={project.id}
                            projectTitle={project.title || "project"}
                            files={videos}
                            channels={project.content_channels}
                            disabled={!canDownload}
                            allowUnapproved={isInternal(role) || !!project.brands?.some((b) => b.pre_approval_downloading)}
                            allowedFileTypes={allowedFileTypes}
                        />
                    )}
                    {isInternal(role) && realVideos.length > 0 && (
                        <Select
                            size="small"
                            displayEmpty
                            value={sharedInternalStatus}
                            SelectDisplayProps={{ "aria-label": "Set all internal status" } as any}
                            onChange={(e) => handleProjectStatusChange(e.target.value)}
                            sx={INTERNAL_STATUS_SELECT_SX}
                        >
                            <MenuItem value="" disabled>
                                <em>Set All Internal Status</em>
                            </MenuItem>
                            {internalStatusOptionsFor(sharedInternalStatus).map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                            <MenuItem value={CLEAR_ALL_STATUS}>
                                <em>Clear All</em>
                            </MenuItem>
                        </Select>
                    )}
                    {/* Each link follows its own page's toggle; the menu
                        hides itself when neither page is on. */}
                    <PublicUrlMenu options={publicUrlOptions} variant="secondary" />

                    {!isMobile && (
                        <ViewToggle
                            view={view}
                            onViewChange={(v) => setView(v as "grid" | "list")}
                            gridValue="grid"
                            listValue="list"
                        />
                    )}
                </Box>
            </Box>

            {hasChannels ? (
                <Box className="mt-6">
                    <Box className="flex gap-0 mb-4">
                        <Typography className="text-text-2 shrink-0 whitespace-nowrap" sx={{ pr: "20px" }}>
                            {channelGroups.length} Channel{channelGroups.length !== 1 ? "s" : ""} -{" "}
                            {filterRealFiles(videos).length}/{(videos || []).length} Video
                            {(videos || []).length !== 1 ? "s" : ""} Delivered
                        </Typography>
                        <div className="border-t w-full border-border-token mt-3" />
                    </Box>
                    {channelGroups.map((group, idx) => (
                        <ChannelAccordion
                            key={group.key}
                            group={group}
                            defaultExpanded={idx === 0}
                            showDelivered
                            headerAction={
                                isInternal(role) && group.channel.brand_project_channel_id != null ? (
                                    <IconButton
                                        aria-label="Edit channel options"
                                        size="small"
                                        onClick={() => setEditingGroup(group)}
                                        sx={{ ml: 0.5 }}
                                    >
                                        <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                ) : undefined
                            }
                        >
                            <Box className="flex flex-col gap-3">
                                {renderVideos(group.videos)}
                                {uploadEnabled && (
                                    <ChannelUploadTile
                                        videos={group.videos}
                                        project={project}
                                        refetch={onProjectUpdate || (() => {})}
                                    />
                                )}
                            </Box>
                        </ChannelAccordion>
                    ))}
                </Box>
            ) : (
                <Box>
                    <Box className="mt-8 flex gap-0">
                        <Typography className="w-[110px] text-text-2">{(videos || []).length} Videos</Typography>
                        <div className="mt-3 border-t w-full border-border-token" />
                    </Box>
                    <Box className="mt-10 flex flex-col gap-3">
                        {renderVideos(videos || [])}
                        {uploadEnabled && (
                            <ChannelUploadTile
                                videos={videos || []}
                                project={project}
                                refetch={onProjectUpdate || (() => {})}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {reviewFile && (
                <VariationReviewModal
                    open={!!reviewFile}
                    project={project}
                    variationFile={reviewFile}
                    onClose={closeReview}
                />
            )}

            {editingGroup && editingGroup.channel.brand_project_channel_id != null && project?.id && (
                <ProjectChannelOptionsModal
                    open
                    onClose={() => setEditingGroup(null)}
                    projectId={project.id}
                    channel={editingGroup.channel}
                    onSaved={onProjectUpdate || (() => {})}
                />
            )}
        </Box>
    );
};
